import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Banner } from "../components/Banner";
import "./AuthForm.css";

interface SetupInfo {
  email: string;
  purpose: string;
}

export function SetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [info, setInfo] = useState<SetupInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadErr("No token in URL. The link you followed may be malformed.");
      return;
    }
    fetch(`/api/auth/setup-info?token=${encodeURIComponent(token)}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `(${r.status})`);
        }
        return (await r.json()) as SetupInfo;
      })
      .then(setInfo)
      .catch((e) => setLoadErr((e as Error).message));
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const resp = await fetch("/api/auth/setup", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setErr(body.error ?? `(${resp.status})`);
        return;
      }
      window.location.href = "/dashboard";
    } catch (e2) {
      setErr((e2 as Error).message ?? "Network error");
    } finally {
      setBusy(false);
    }
  }

  if (loadErr) {
    return (
      <Layout>
        <div className="auth-form-wrap">
          <Card>
            <div className="auth-form-head">
              <span className="label">Set up account</span>
              <h1>This link isn't usable</h1>
              <p style={{ color: "var(--color-bone-dim)", margin: "8px 0 0" }}>
                It may have expired or already been used.
              </p>
            </div>
            <p style={{ marginTop: "var(--space-3)" }}>
              <a href="/login">Sign in</a> if you already have an account — otherwise ask your
              Retrofit AI contact for a fresh setup link.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="auth-form-wrap">
        <Card>
          <div className="auth-form-head">
            <span className="label">Retrofit AI Monitor</span>
            <h1>Set your password</h1>
            <p style={{ color: "var(--color-bone-dim)", margin: "8px 0 0" }}>
              {info ? <>For <code>{info.email}</code>. You'll use this to sign in from now on.</> : <>Loading…</>}
            </p>
          </div>

          {err && <Banner tone="err">{err}</Banner>}

          <form className="auth-form" onSubmit={submit}>
            <label>
              <span className="auth-form__label">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy || !info}
              />
            </label>
            <label>
              <span className="auth-form__label">Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy || !info}
              />
            </label>
            <Button type="submit" disabled={busy || !info}>
              {busy ? "Setting password…" : "Set password & sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </Layout>
  );
}
